'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useContext, useMemo } from 'react';

// 导入统一的类型定义
import { BaseIndexData, DisplayCondition, HomePageTitleProps, StyleVariant } from './types';

// 重新导出类型，供其他组件使用
export type { BaseIndexData, DisplayCondition, HomePageTitleProps, StyleVariant };

// 处理换行的辅助函数
const processLineBreaks = (text: string): string => {
  return text.replace(/\n/g, '<br>');
};

// 样式映射配置
const getStyleConfig = (
  variant: StyleVariant,
  isPC: boolean,
  noBtn: boolean,
  isNew: boolean,
  hasButton: boolean,
) => {
  if (variant === 'inline' && isPC) {
    // PC端横向排列样式 - 根据是否有按钮控制宽度
    let containerWidth = 'w-[90%]';
    if (!noBtn && hasButton) {
      containerWidth = 'w-[80%]';
    }

    return {
      container: `flex items-start 2xl:items-center flex-wrap 2xl:flex-nowrap ${containerWidth}`,
      title:
        'text-left font-bb pr-[10px] lg:pr-[20px] text-[24px] md:text-[28px] lg:text-[32px] xl:text-[36px] 2xl:text-[40px] 3xl:text-[40px] leading-[29px] md:leading-[34px] lg:leading-[38px] xl:leading-[43px] 2xl:leading-[48px] 3xl:leading-[48px]',
      subtitle:
        'text-left font-br mt-[8px] 2xl:mt-0 p-0 text-[12px] md:text-[13px] lg:text-[14px] xl:text-[15px] 2xl:text-[16px] 3xl:text-[16px] leading-[14px] md:leading-[15px] lg:leading-[17px] xl:leading-[18px] 2xl:leading-[19px] 3xl:leading-[19px] line-clamp-1',
    };
  }

  // 默认竖直排列样式
  const mobileContainerClass = isNew ? 'text-center px-[19px]' : 'text-center px-[19px]';
  const mobileSubtitleMt = isNew ? 'mt-[4px]' : 'mt-[7px]';

  return {
    container: isPC ? 'text-center px-[14px]' : mobileContainerClass,
    title: isPC
      ? 'font-bb text-[24px] md:text-[28px] lg:text-[32px] xl:text-[36px] 2xl:text-[40px] 3xl:text-[40px] leading-[29px] md:leading-[34px] lg:leading-[38px] xl:leading-[43px] 2xl:leading-[48px] 3xl:leading-[48px]'
      : 'font-bb text-[24px] leading-[29px] text-center whitespace-pre-line',
    subtitle: isPC
      ? 'font-br text-[12px] md:text-[13px] lg:text-[14px] xl:text-[15px] 2xl:text-[16px] 3xl:text-[16px] leading-[14px] md:leading-[15px] lg:leading-[17px] xl:leading-[18px] 2xl:leading-[19px] 3xl:leading-[19px] mt-[6px] md:mt-[6px] lg:mt-[7px] xl:mt-[7px] 2xl:mt-[8px] 3xl:mt-[8px] line-clamp-1'
      : `font-br text-[12px] leading-[18px] ${mobileSubtitleMt} text-center whitespace-pre-line line-clamp-3`,
  };
};

/**
 * 统一的首页标题组件
 * 支持PC端两种主要样式：默认竖直排列和横向排列
 * 职责单一：只负责标题渲染，不处理按钮和外部间距
 */
export default function HomePageTitle({
  indexData,
  variant = 'default',
  displayCondition = 'all',
  titleTag: TitleTag = 'h2',
  subtitleTag: SubtitleTag = 'h5',
  className = '',
  titleClassName = '',
  subtitleClassName = '',
  noBtn = false,
  isNew = false,
  hasButton = false,
  customStyles = {},
}: HomePageTitleProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 检查显示条件
  const shouldDisplay = useMemo(() => {
    if (!indexData?.layout_title || indexData?.layout_title_status !== 1) {
      return false;
    }

    switch (displayCondition) {
      case 'pc-only':
        return isPC;
      case 'mobile-only':
        return !isPC;
      case 'all':
      default:
        return true;
    }
  }, [indexData, displayCondition, isPC]);

  // 获取样式配置
  const styleConfig = useMemo(
    () => getStyleConfig(variant, isPC, noBtn, isNew, hasButton),
    [variant, isPC, noBtn, isNew, hasButton],
  );

  // 合并样式
  const finalTitleStyle = useMemo(
    () => ({
      color: indexData.layout_title_color || '#282828',
      ...customStyles.title,
    }),
    [indexData.layout_title_color, customStyles.title],
  );

  const finalSubtitleStyle = useMemo(
    () => ({
      color: indexData.layout_subtitle_color || '#282828',
      ...customStyles.subtitle,
    }),
    [indexData.layout_subtitle_color, customStyles.subtitle],
  );

  if (!shouldDisplay) {
    return null;
  }

  return (
    <div className={`${styleConfig.container} ${className}`} style={customStyles.container}>
      {/* 横向排列样式 */}
      {variant === 'inline' && isPC ? (
        <div className="flex flex-col 2xl:flex-row 2xl:items-center w-full">
          <TitleTag
            className={`${styleConfig.title} ${titleClassName}`}
            style={finalTitleStyle}
            dangerouslySetInnerHTML={{
              __html: processLineBreaks(indexData.layout_title),
            }}
          />
          {indexData.layout_subtitle && (
            <SubtitleTag
              className={`${styleConfig.subtitle} ${subtitleClassName}`}
              style={finalSubtitleStyle}
              dangerouslySetInnerHTML={{
                __html: processLineBreaks(indexData.layout_subtitle),
              }}
            />
          )}
        </div>
      ) : (
        /* 默认竖直排列样式 */
        <>
          <TitleTag
            className={`${styleConfig.title} ${titleClassName}`}
            style={finalTitleStyle}
            dangerouslySetInnerHTML={{
              __html: processLineBreaks(indexData.layout_title),
            }}
          />
          {indexData.layout_subtitle && (
            <SubtitleTag
              className={`${styleConfig.subtitle} ${subtitleClassName}`}
              style={finalSubtitleStyle}
              dangerouslySetInnerHTML={{
                __html: processLineBreaks(indexData.layout_subtitle),
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
